import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../../lib/api";
import { DialogPanel } from "../../../components/shared/DialogPanel";
import { XIcon } from "../../../components/shared/Icons";
import { backdropClose } from "../../../components/shared/backdrop";

interface PermGroup {
  label: string;
  items: [string, string][];
}

const PERM_GROUPS: PermGroup[] = [
  {
    label: "Dashboard",
    items: [
      ["dash_geral", "Acesso à Dash Geral (todos os projetos, visão completa)"],
    ],
  },
  {
    label: "Relatórios",
    items: [
      ["relatorios", "Acesso à aba Relatórios (visão gerencial de todos os gestores)"],
      ["relatorios_participa", "Participa dos relatórios (os projetos que este usuário possui entram nos dados)"],
    ],
  },
  {
    label: "Financeiro",
    items: [
      ["financeiro", "Acesso ao painel Financeiro (leads por agente, limite e valor por lead extra)"],
    ],
  },
  {
    label: "Agentes",
    items: [
      ["agente_excluir", "Excluir agentes"],
      ["agente_participantes", "Gerenciar participantes em agentes"],
      ["tool_publicar", "Publicar tools de agentes como modelos do sistema"],
      ["agente_alterar_proprietario", "Alterar proprietário de agentes"],
      ["agente_resolver_alerta", "Dar dismiss em alertas de erro (envia para aprovação do admin)"],
    ],
  },
  {
    label: "Kanban (Quadro v2)",
    items: [
      ["kanban_ver_todos", "Ver todos os cards do kanban (edita só os seus ou em que participa)"],
      ["kanban_total", "Controle total do kanban (edita e exclui qualquer card, administra assuntos)"],
    ],
  },
  {
    label: "API Estoque",
    items: [
      ["estoque_criar", "Criar clientes de estoque"],
      ["estoque_participantes", "Gerenciar participantes em clientes de estoque"],
      ["estoque_alterar_proprietario", "Alterar proprietário de clientes de estoque"],
      ["estoque_excluir_cliente", "Excluir clientes do painel API"],
    ],
  },
  {
    label: "Patioo",
    items: [
      ["patioo_acesso", "Acesso à aba Patioo (ícone visível no menu; dono/participante ainda precisa desta)"],
      ["patioo_criar", "Criar Patioo (config por loja)"],
      ["patioo_participantes", "Gerenciar participantes em Patioos"],
      ["patioo_alterar_proprietario", "Alterar proprietário de Patioos"],
      ["patioo_excluir", "Excluir Patioos"],
    ],
  },
  {
    label: "WABA",
    items: [
      ["waba_criar", "Criar conexões WABA"],
      ["waba_excluir", "Excluir conexões WABA"],
      ["waba_participantes", "Gerenciar participantes em conexões WABA"],
      ["waba_alterar_proprietario", "Alterar proprietário de conexões WABA"],
      ["waba_criar_template", "Criar templates de mensagem"],
      ["waba_excluir_template", "Excluir templates de mensagem"],
      ["waba_excluir_webhook", "Excluir webhooks de qualquer conexão WABA"],
      ["waba_metricas_todas", "Ver métricas de todas as conexões WABA"],
      ["waba_ver_todas", "Ver todas as conexões WABA (sem excluir ou trocar proprietário)"],
    ],
  },
  {
    label: "Painel Dev",
    items: [
      ["dev_acesso", "Acesso ao Painel de Dev (ícone visível no menu)"],
      ["dev_variaveis", "Variáveis globais"],
      ["dev_subagentes", "Sub-agentes"],
      ["dev_modelos", "Modelos disponíveis"],
      ["dev_tools", "Modelos de Tools"],
      ["dev_prompt_templates", "Templates de Prompts"],
      ["dev_modelos_leitura", "Ver Modelos de Tools e Templates de Agentes (somente leitura)"],
      ["dev_helena", "Portal Helena (contas do portal de clientes)"],
    ],
  },
  {
    label: "Configurações",
    items: [
      ["config_equipe", "Gerenciamento de equipe (criar/editar/excluir usuários)"],
      ["config_funcoes", "Gerenciamento de funções (esta página)"],
      ["config_apis", "APIs"],
      ["config_logs", "Logs"],
      ["config_tags", "Gerenciamento global de tags"],
    ],
  },
];

const ALL_PERMS: [string, string][] = PERM_GROUPS.flatMap((g) => g.items);

interface Role { id: string; name: string; permissions: Record<string, boolean>; }

export const Route = createFileRoute("/_authed/settings/roles")({
  component: RolesPage,
});

const btnPrimary: React.CSSProperties = {
  background: "var(--brand)", color: "#fff", padding: "7px 14px",
  borderRadius: 8, fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer",
};
const btnGhost: React.CSSProperties = {
  background: "none", color: "var(--ink-secondary)", padding: "7px 14px",
  borderRadius: 8, fontSize: 13, border: "1px solid var(--line-strong)", cursor: "pointer",
};
const inputStyle: React.CSSProperties = {
  padding: "8px 12px", background: "var(--surface-input)", border: "1px solid var(--line)",
  borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", width: "100%",
  boxSizing: "border-box",
};

function RoleModal({ initial, onClose, onSaved }: { initial: Role; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial.name);
  const [perms, setPerms] = useState<Record<string, boolean>>(initial.permissions);
  const mut = useMutation({
    mutationFn: async () => {
      const body = { name, permissions: perms };
      return initial.id ? api.patch(`/roles/${initial.id}`, body) : api.post("/roles", body);
    },
    onSuccess: onSaved,
  });

  const permCount = ALL_PERMS.filter(([k]) => perms[k]).length;
  const isViewer = !!perms.viewer;

  return (
    <div {...backdropClose(() => { onClose(); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <DialogPanel onClose={() => onClose()} label={initial.id ? "Editar função" : "Nova função"} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 520, maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>{initial.id ? "Editar função" : "Nova função"}</span>
          <button aria-label="Fechar" onClick={onClose} style={{ color: "var(--ink-muted)", background: "none", border: "none", fontSize: 16, cursor: "pointer" }}><XIcon size={14} /></button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px", display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label htmlFor="role-name" style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Nome da função</label>
            <input id="role-name" style={inputStyle} placeholder="Nome da função" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          {/* Modo Viewer — exclusivo */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "12px 14px", borderRadius: 10, background: isViewer ? "var(--brand-soft)" : "var(--surface-card)", border: `1px solid ${isViewer ? "var(--brand)" : "var(--line)"}` }}>
            <input
              type="checkbox"
              checked={isViewer}
              onChange={(e) => setPerms(e.target.checked ? { viewer: true } : {})}
              style={{ accentColor: "var(--brand)", width: 14, height: 14, marginTop: 1, flexShrink: 0 }}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: isViewer ? "var(--brand)" : "var(--ink)" }}>Modo Viewer</div>
              <div style={{ fontSize: 12, color: "var(--ink-muted)", lineHeight: 1.4 }}>
                Somente-leitura: vê todos os agentes e a dashboard completa, sem editar, pausar ou excluir nada. Sem Painel Dev, Configurações ou Estoque. Marcar desabilita as demais permissões.
              </div>
            </div>
          </label>

          <div style={{ display: "flex", flexDirection: "column", gap: 20, opacity: isViewer ? 0.35 : 1, pointerEvents: isViewer ? "none" : "auto" }}>
          {PERM_GROUPS.map((group) => (
            <div key={group.label}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                {group.label}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7, paddingLeft: 2 }}>
                {group.items.map(([key, label]) => (
                  <label key={key} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      disabled={isViewer}
                      checked={!!perms[key]}
                      onChange={(e) => setPerms({ ...perms, [key]: e.target.checked })}
                      style={{ accentColor: "var(--brand)", width: 14, height: 14, marginTop: 1, flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: "1.4" }}>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--line)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>{permCount} permissão{permCount !== 1 ? "ões" : ""} selecionada{permCount !== 1 ? "s" : ""}</span>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={btnGhost} onClick={onClose}>Cancelar</button>
            <button style={btnPrimary} onClick={() => mut.mutate()} disabled={mut.isPending}>
              {mut.isPending ? "..." : "Salvar"}
            </button>
          </div>
        </div>
      </DialogPanel>
    </div>
  );
}

function RolesPage() {
  const qc = useQueryClient();
  const { data: roles } = useQuery<Role[]>({ queryKey: ["roles"], queryFn: () => api.get("/roles") });
  const [editing, setEditing] = useState<Role | null>(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h1 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Funções</h1>
        <button style={btnPrimary} onClick={() => setEditing({ id: "", name: "", permissions: {} })}>
          + Nova função
        </button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {roles?.map((r) => {
          const isAdmin = r.name === "Admin";
          const isViewerRole = !!r.permissions.viewer;
          const count = ALL_PERMS.filter(([k]) => r.permissions[k]).length;
          return (
            <div
              key={r.id}
              style={{
                background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8,
                padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10,
                cursor: isAdmin ? "default" : "pointer",
                opacity: 1,
              }}
              onClick={() => !isAdmin && setEditing(r)}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontWeight: 600 }}>{r.name}</span>
                  {isAdmin && (
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--warn-soft)", color: "var(--warn)", border: "1px solid var(--warn-line)" }}>
                      Acesso total
                    </span>
                  )}
                  {isViewerRole && (
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--brand-soft)", color: "var(--brand)", border: "1px solid var(--brand)" }}>
                      Somente leitura
                    </span>
                  )}
                </div>
                {!isAdmin && (
                  <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
                    {count} permissão{count !== 1 ? "ões" : ""}
                  </span>
                )}
              </div>
              {!isAdmin && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {ALL_PERMS.filter(([k]) => r.permissions[k]).slice(0, 8).map(([k, label]) => (
                    <span
                      key={k}
                      style={{
                        fontSize: 11, padding: "2px 8px", borderRadius: 99,
                        background: "var(--brand-soft)", color: "var(--brand)", border: "1px solid var(--brand)",
                      }}
                    >
                      {label}
                    </span>
                  ))}
                  {count > 8 && (
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--surface-input)", color: "var(--ink-muted)", border: "1px solid var(--line-strong)" }}>
                      +{count - 8} mais
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <RoleModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["roles"] }); setEditing(null); }}
        />
      )}
    </div>
  );
}
